import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Receipt, Upload, CheckCircle, XCircle, Clock, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkerExpenses = () => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [claims, setClaims] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchClaims = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/worker/expense-claims`, { withCredentials: true });
      setClaims(res.data);
    } catch (e) { /* silenced */ }
  }, []);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!amount || !description) return alert('Importo e descrizione obbligatori');
    setSubmitting(true);
    try {
      let receipt_base64 = '';
      let receipt_filename = '';
      if (receiptFile) {
        const reader = new FileReader();
        receipt_base64 = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(receiptFile);
        });
        receipt_filename = receiptFile.name;
      }

      await axios.post(`${API_URL}/api/worker/expense-claims`, {
        amount: parseFloat(amount),
        description,
        receipt_base64,
        receipt_filename,
      }, { withCredentials: true });

      setAmount('');
      setDescription('');
      setReceiptFile(null);
      setReceiptPreview(null);
      fetchClaims();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore invio spesa');
    } finally { setSubmitting(false); }
  };

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle className="w-4 h-4 text-[#34C759]" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4 text-[#FF3B30]" />;
    return <Clock className="w-4 h-4 text-[#FF9500]" />;
  };

  const statusLabel = (status) => {
    if (status === 'approved') return 'Approvata';
    if (status === 'rejected') return 'Rifiutata';
    return 'In attesa';
  };

  return (
    <Card className="border-[#E4E4E7] shadow-sm" data-testid="worker-expenses-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#FF9500]" />
          Spese Extra
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#09090B]">Importo (€)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="expense-amount-input"
              className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9500]"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-[#09090B]">Ricevuta</label>
            <label
              className="flex items-center justify-center gap-1 px-3 py-2 border border-dashed border-[#E4E4E7] rounded-md text-sm text-[#71717A] cursor-pointer hover:border-[#FF9500] hover:text-[#FF9500] transition-colors"
              data-testid="expense-receipt-label"
            >
              <Upload className="w-4 h-4" />
              {receiptFile ? receiptFile.name.slice(0, 15) : 'Carica foto'}
              <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="expense-receipt-input" />
            </label>
          </div>
        </div>

        {receiptPreview && (
          <div className="relative">
            <img src={receiptPreview} alt="Ricevuta" className="w-full max-h-32 object-cover rounded-md border border-[#E4E4E7]" />
            <button
              onClick={() => { setReceiptFile(null); setReceiptPreview(null); }}
              className="absolute top-1 right-1 bg-white/80 rounded-full p-0.5 text-[#FF3B30] hover:bg-white"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-sm font-medium text-[#09090B]">Descrizione</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Es: Materiale cantiere, Carburante..."
            data-testid="expense-description-input"
            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9500]"
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting || !amount || !description}
          data-testid="expense-submit-btn"
          className="w-full bg-[#FF9500] hover:bg-[#E68600] text-white"
        >
          <Send className="w-4 h-4 mr-2" />
          {submitting ? 'Invio...' : 'Invia Spesa'}
        </Button>

        {/* Claims history */}
        {claims.length > 0 && (
          <div className="pt-3 border-t border-[#E4E4E7]">
            <h4 className="text-sm font-semibold text-[#09090B] mb-2">Le mie spese</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {claims.map(c => (
                <div key={c.claim_id} className="flex items-start justify-between p-2 bg-[#F4F4F5] rounded-md" data-testid={`expense-item-${c.claim_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {statusIcon(c.status)}
                      <span className="text-xs font-bold text-[#09090B]">€{c.amount.toFixed(2)}</span>
                      <span className="text-xs text-[#71717A]">— {statusLabel(c.status)}</span>
                    </div>
                    <p className="text-xs text-[#71717A] mt-0.5 truncate">{c.description}</p>
                    {c.reject_reason && <p className="text-xs text-[#FF3B30] mt-0.5">Motivo: {c.reject_reason}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkerExpenses;
